import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, Clock, CalendarDays, Sun, Ban } from "lucide-react";
import { Navigation } from "./Navigation";
import { Footer } from "./Footer";
import { CtaButton } from "./CtaButton";
import { Reveal } from "./motion-primitives";
import { DetailSection, DestinationTile } from "./destinations-components";
import { getBySlug } from "./destinations-catalog";
import { useSeo } from "./useSeo";

const EXPERIENCE_ORDER = [
  ["do", "Do"],
  ["see", "See"],
  ["eat", "Eat"],
  ["drink", "Drink"],
  ["stay", "Stay"],
  ["wander", "Wander"],
];

export const DestinationDetailPage = () => {
  const { slug } = useParams();
  const d = getBySlug(slug);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  useSeo(
    d
      ? d.seo
      : { title: "Destination not found | Holideviser", description: "" },
  );

  if (!d) {
    return (
      <div className="min-h-screen bg-ink" data-testid="destination-unpublished">
        <Navigation />
        <div className="mx-auto flex min-h-[60vh] max-w-2xl flex-col items-center justify-center px-6 text-center">
          <h1 className="font-serif text-4xl font-light text-cream">We haven't been there yet.</h1>
          <p className="mt-4 font-sans text-sand">
            This guide isn't published yet. Explore the destinations we've personally experienced.
          </p>
          <Link
            to="/destinations"
            data-testid="unpublished-directory-link"
            className="mt-8 border-b border-teal pb-1 font-sans text-sm uppercase tracking-[0.18em] text-cream hover:text-teal"
          >
            Browse destinations
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const planLink = `/plan-my-trip?destination=${encodeURIComponent(d.name)}`;

  return (
    <div className="min-h-screen bg-ink" data-testid="destination-detail">
      <Navigation />

      {/* Hero */}
      <section
        ref={heroRef}
        className="relative h-[86svh] min-h-[560px] w-full overflow-hidden"
        data-testid="detail-hero"
      >
        <motion.div style={{ y, scale }} className="absolute inset-0">
          <img src={d.image} alt={`${d.name}, ${d.country}`} className="h-full w-full object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/70 via-transparent to-transparent" />
        <div className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-end px-6 pb-16 sm:px-10 lg:px-16 lg:pb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
          >
            <div className="mb-4 flex items-center gap-4">
              <span className="h-px w-10 bg-teal" />
              <span className="font-sans text-xs uppercase tracking-[0.3em] text-cream/80">
                {d.country} · {d.region}
              </span>
            </div>
            <h1
              data-testid="detail-h1"
              className="font-serif text-6xl font-light leading-[0.98] tracking-tight text-cream sm:text-7xl lg:text-8xl"
            >
              {d.name}
            </h1>
            <p className="mt-6 max-w-2xl font-serif text-xl font-light italic leading-snug text-cream/85 sm:text-2xl">
              “{d.tagline}”
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {d.tags.map((t) => (
                <span
                  key={t}
                  className="rounded-full border border-cream/25 px-4 py-1.5 font-sans text-xs text-cream/80"
                >
                  {t}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Our point of view */}
      <DetailSection id="point-of-view" overline="Our point of view">
        <Reveal>
          <p className="max-w-4xl font-serif text-2xl font-light leading-snug text-cream/90 sm:text-3xl lg:text-4xl">
            {d.pov}
          </p>
        </Reveal>
      </DetailSection>

      {/* Why go */}
      <DetailSection id="why-go" overline="Why go" title="What makes it worth it.">
        <div className="grid grid-cols-1 gap-px overflow-hidden border-y border-charcoal sm:grid-cols-2 lg:grid-cols-4">
          {d.whyGo.map((w, i) => (
            <Reveal key={w.title} delay={i * 0.06} className="bg-ink p-8">
              <span className="font-serif text-2xl font-light text-teal">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="mt-6 font-sans text-sm uppercase tracking-[0.22em] text-cream">
                {w.title}
              </h3>
              <p className="mt-3 font-sans text-sm leading-relaxed text-sand">{w.note}</p>
            </Reveal>
          ))}
        </div>
      </DetailSection>

      {/* Who it's for */}
      <DetailSection id="who-for" overline="Who it's for" title="You'll love this if…">
        <ul className="max-w-3xl divide-y divide-charcoal border-y border-charcoal">
          {d.whoFor.map((w, i) => (
            <Reveal as="li" key={i} delay={i * 0.05} className="flex items-start gap-5 py-6">
              <span className="mt-1 h-2 w-2 shrink-0 bg-teal" />
              <span className="font-serif text-xl font-light leading-snug text-cream/90 sm:text-2xl">
                {w}
              </span>
            </Reveal>
          ))}
        </ul>
      </DetailSection>

      {/* When to go */}
      <DetailSection id="when-to-go" overline="When to go" title="Time it right.">
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
          <InfoStat icon={<CalendarDays size={16} />} label="Best months" value={d.whenToGo.bestMonths} />
          <InfoStat icon={<Sun size={16} />} label="Shoulder season" value={d.whenToGo.shoulder} />
          <InfoStat icon={<Sun size={16} />} label="Peak season" value={d.whenToGo.peak} />
          <div className="sm:col-span-2 lg:col-span-3">
            <p className="border-t border-charcoal pt-6 font-sans text-base leading-relaxed text-sand">
              {d.whenToGo.weather}
            </p>
            {d.whenToGo.events?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {d.whenToGo.events.map((e) => (
                  <span
                    key={e}
                    className="rounded-full border border-charcoal px-4 py-1.5 font-sans text-xs text-cream/80"
                  >
                    {e}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </DetailSection>

      {/* How long to stay */}
      <DetailSection id="how-long" overline="How long to stay" title="Give it the right amount of time.">
        <div className="grid grid-cols-1 gap-px overflow-hidden border-y border-charcoal sm:grid-cols-3">
          {d.tripLengths.map((t, i) => (
            <Reveal key={t.days} delay={i * 0.06} className="bg-ink p-8">
              <h3 className="font-serif text-3xl font-light text-cream">{t.days}</h3>
              <p className="mt-3 font-sans text-sm leading-relaxed text-sand">{t.note}</p>
            </Reveal>
          ))}
        </div>
      </DetailSection>

      {/* The Holideviser experience */}
      <DetailSection
        id="experience"
        overline="The Holideviser experience"
        title="What we'd actually do."
      >
        <div className="grid grid-cols-1 gap-x-10 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
          {EXPERIENCE_ORDER.map(([key, label], i) => (
            <Reveal key={key} delay={i * 0.05}>
              <h3 className="border-b border-charcoal pb-3 font-sans text-xs uppercase tracking-[0.28em] text-teal">
                {label}
              </h3>
              <ul className="mt-4 flex flex-col gap-3">
                {(d.experiences[key] || []).map((item, j) => (
                  <li key={j} className="font-sans text-base leading-relaxed text-cream/85">
                    {item}
                  </li>
                ))}
              </ul>
            </Reveal>
          ))}
        </div>
      </DetailSection>

      {/* What we'd skip */}
      <DetailSection id="skip" overline="What we'd skip" title="Save your time.">
        <ul className="max-w-3xl flex flex-col gap-5" data-testid="skip-list">
          {d.skip.map((s, i) => (
            <Reveal as="li" key={i} delay={i * 0.05} className="flex items-start gap-4">
              <Ban size={18} className="mt-1 shrink-0 text-teal" />
              <span className="font-sans text-base leading-relaxed text-cream/85 sm:text-lg">{s}</span>
            </Reveal>
          ))}
        </ul>
      </DetailSection>

      {/* Where to stay */}
      <DetailSection id="where-to-stay" overline="Where to stay" title="A room that fits the trip.">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {d.whereToStay.map((s, i) => (
            <Reveal key={i} delay={i * 0.06} className="flex flex-col border border-charcoal p-7">
              <span className="font-sans text-xs uppercase tracking-[0.22em] text-teal">
                {s.audience}
              </span>
              <h3 className="mt-4 font-serif text-2xl font-light text-cream">{s.hotel}</h3>
              <p className="mt-1 font-sans text-xs uppercase tracking-[0.16em] text-sand">
                {s.neighborhood}
              </p>
              <p className="mt-4 font-sans text-sm leading-relaxed text-cream/80">{s.why}</p>
              <p className="mt-auto pt-6 font-sans text-xs uppercase tracking-[0.16em] text-sand">
                {s.price}
              </p>
            </Reveal>
          ))}
        </div>
      </DetailSection>

      {/* Food & drink */}
      <DetailSection id="food-drink" overline="Food & drink" title="What to eat here.">
        <div className="grid grid-cols-1 gap-x-10 gap-y-10 sm:grid-cols-2">
          {d.foodDrink.map((f, i) => (
            <Reveal key={f.category} delay={i * 0.05}>
              <h3 className="border-b border-charcoal pb-3 font-sans text-xs uppercase tracking-[0.28em] text-teal">
                {f.category}
              </h3>
              <ul className="mt-4 flex flex-col gap-2">
                {f.items.map((item, j) => (
                  <li key={j} className="font-sans text-base leading-relaxed text-cream/85">
                    {item}
                  </li>
                ))}
              </ul>
            </Reveal>
          ))}
        </div>
      </DetailSection>

      {/* Suggested trip styles */}
      <DetailSection
        id="trip-styles"
        overline="Suggested trip styles"
        title={`Ways to experience ${d.name}.`}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2">
          {d.suggestedTripStyles.map((t, i) => (
            <Reveal key={t.title} delay={i * 0.05}>
              <Link
                to={planLink}
                data-testid={`trip-style-${i}`}
                className="group flex items-center justify-between gap-4 border border-charcoal p-7 transition-colors hover:border-teal/50"
              >
                <div>
                  <h3 className="font-serif text-2xl font-light text-cream">{t.title}</h3>
                  <p className="mt-2 font-sans text-sm leading-relaxed text-sand">{t.note}</p>
                </div>
                <ArrowUpRight
                  size={18}
                  className="shrink-0 text-sand transition-colors group-hover:text-teal"
                />
              </Link>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="mt-8 font-sans text-sm text-sand">
            Looking for inspiration first?{" "}
            <Link to="/discoverography" className="text-teal underline-offset-4 hover:underline">
              Explore Discoverography
            </Link>
            .
          </p>
        </Reveal>
      </DetailSection>

      {/* Related destinations */}
      <DetailSection id="related" overline="You might also like" title="If you loved this.">
        <div className="grid grid-cols-1 gap-x-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
          {d.relatedSlugs.map((rs) => {
            const r = getBySlug(rs);
            if (!r) return null;
            return <DestinationTile key={rs} d={r} testId={`related-tile-${rs}`} />;
          })}
        </div>
      </DetailSection>

      {/* Plan my trip CTA */}
      <section className="bg-teal text-ink" data-testid="detail-plan-cta-section">
        <div className="mx-auto max-w-[1400px] px-6 py-24 sm:px-10 lg:px-16 lg:py-32">
          <Reveal>
            <span className="font-sans text-xs uppercase tracking-[0.3em] text-ink/60">
              Make it yours
            </span>
          </Reveal>
          <Reveal delay={0.06}>
            <h2 className="mt-8 max-w-3xl font-serif text-5xl font-light leading-[1.02] tracking-tight text-ink sm:text-6xl">
              Ready to make it yours?
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-8 max-w-xl font-sans text-lg leading-relaxed text-ink/80">
              Tell us how you want to travel. We'll take it from here.
            </p>
          </Reveal>
          <Reveal delay={0.18}>
            <div className="mt-12">
              <CtaButton testId="detail-plan-cta" variant="onTeal" to={planLink}>
                Plan My Trip
              </CtaButton>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const InfoStat = ({ icon, label, value }) => (
  <div className="flex items-start gap-3">
    <span className="mt-1 text-teal">{icon}</span>
    <div>
      <p className="font-sans text-[0.65rem] uppercase tracking-[0.2em] text-sand">{label}</p>
      <p className="mt-1 font-serif text-xl font-light text-cream">{value}</p>
    </div>
  </div>
);
